import { NavLink, useLocation } from 'react-router-dom';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';

// Name label that pops out to the right of an icon when the sidebar is collapsed
const Tip = ({ show, label, children }) => (show ? (
  <Tooltip>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent side="right" sideOffset={10} className="bg-slate-900 text-white text-xs font-semibold px-2.5 py-1.5" data-testid="ws-tooltip">{label}</TooltipContent>
  </Tooltip>
) : children);
import { ChevronsLeft, ChevronsRight, Search, Plus } from 'lucide-react';
import { WORKSPACE_GROUPS, ADMIN_GROUP, isItemActive } from '@/lib/workspaceNav';

export default function WorkspaceSidebar({ user, collapsed, onToggle, onSearch, onNavigate }) {
  const { pathname, search } = useLocation();
  const groups = user?.role === 'admin' ? [...WORKSPACE_GROUPS, ADMIN_GROUP] : WORKSPACE_GROUPS;
  return (
    <TooltipProvider delayDuration={0}>
    <aside data-testid="ws-sidebar" data-collapsed={collapsed}
      className={`flex flex-col h-full bg-white border-r border-slate-200 transition-[width] duration-200 ${collapsed ? 'w-[68px]' : 'w-64'}`}>
      <div className={`px-4 pt-5 pb-3 ${collapsed ? 'px-2' : ''}`}>
        {!collapsed && (
          <div className="mb-4" data-testid="ws-business-name">
            <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-gold">My workspace</p>
            <p className="font-display text-lg font-semibold text-slate-900 truncate">{user?.business_name || user?.name || 'Your business'}</p>
          </div>
        )}
        <Tip show={collapsed} label="Search (Ctrl K)"><button type="button" onClick={onSearch} data-testid="ws-search-btn"
          className={`w-full flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 text-slate-500 text-sm hover:border-slate-300 transition-colors ${collapsed ? 'justify-center p-2' : 'px-3 py-2'}`}>
          <Search size={15} />{!collapsed && <><span className="flex-1 text-left">Search…</span><kbd className="text-[10px] font-mono bg-white border border-slate-200 rounded px-1">Ctrl K</kbd></>}
        </button></Tip>
        <Tip show={collapsed} label="New quote"><NavLink to="/estimator" onClick={onNavigate} data-testid="ws-new-quote"
          className={`mt-2 tm-btn w-full min-h-0 py-2 text-sm ${collapsed ? 'px-0' : ''}`}>
          <Plus size={15} />{!collapsed && 'New quote'}
        </NavLink></Tip>
      </div>
      <nav className="flex-1 overflow-y-auto px-2 pb-4">
        {groups.map((g) => (
          <div key={g.id} className="mt-3 first:mt-0" data-testid={`ws-group-${g.id}`}>
            {!collapsed && <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">{g.label}</p>}
            {g.items.map((it) => {
              const active = isItemActive(it, pathname, search);
              return (
                <Tip key={it.to} show={collapsed} label={it.label}><NavLink to={it.to} onClick={onNavigate} data-testid={it.tid} data-active={active}
                  className={`group flex items-center gap-3 rounded-lg text-sm transition-colors ${collapsed ? 'justify-center p-2.5' : 'px-3 py-2'} ${
                    active ? 'bg-brand/10 text-brand font-semibold' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}>
                  <it.Icon size={17} strokeWidth={active ? 2.2 : 1.8} className="shrink-0" />
                  {!collapsed && <span className="truncate">{it.label}</span>}
                </NavLink></Tip>
              );
            })}
          </div>
        ))}
      </nav>
      {onToggle && (
        <Tip show={collapsed} label="Expand menu"><button type="button" onClick={onToggle} data-testid="ws-collapse-btn"
          className="hidden lg:flex items-center justify-center gap-2 border-t border-slate-200 py-3 text-xs text-slate-500 hover:text-slate-900">
          {collapsed ? <ChevronsRight size={16} /> : <><ChevronsLeft size={16} /> Collapse</>}
        </button></Tip>
      )}
    </aside>
    </TooltipProvider>
  );
}
